import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "../../../Contexts/TenantContext";
import { orderService } from "../../../Api/Services/orderService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { SimplifiedOrder } from "../../../Models/Order";
import { OrderPaymentDetailsResponse } from "../../../Models/Payment";
import { ShippingTrackingResponse } from "../../../Models/ShippingTracking";
import { PaymentMethod } from "../../../Enums/PaymentMethod";
import { formatDateTimeToBR } from "../../../Utils/DataFormatters";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import OrderCard from "../../../Components/OrderCard/OrderCard";
import Footer from "../../../Components/Footer/Footer";
import PaymentModal from "../../../Components/PaymentModal/PaymentModal";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";
import ShippingTrackingEventsModal from "../../../Components/ShippingTrackingEventsModal/ShippingTrackingEventsModal";
import Button, { buttonClasses } from "../../../Components/Button/Button";

const UserOrderPage = () => {
  const { tenantPreference } = useTenant();
  const [orders, setOrders] = useState<SimplifiedOrder[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);

  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState<OrderPaymentDetailsResponse | null>(null);
  const [isTrackingModalOpen, setTrackingModalOpen] = useState(false);
  const [isTrackingLoading, setTrackingLoading] = useState(false);
  const [trackingData, setTrackingData] = useState<ShippingTrackingResponse | null>(null);

  useEffect(() => {
    loadOrders(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
        }
      } else {
        startPolling();
        refreshOrders();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    startPolling();
    startHub();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      if (hubRef.current) {
        hubRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [currentPage]);

  const loadOrders = async (page: number) => {
    try {
      setIsLoading(true);
      const data = await orderService.getByCustomer(page, pageSize);
      setOrders(data.items);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
      setLastUpdated(new Date());
    } finally {
      setIsLoading(false);
    }
  };

  const refreshOrders = async () => {
    if (isLoading || isRefreshing) {
      return;
    }
    setIsRefreshing(true);
    await loadOrders(currentPage);
    setIsRefreshing(false);
  };

  const startPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }
    pollRef.current = setInterval(() => {
      if (!document.hidden) {
        refreshOrders();
      }
    }, 60000);
  };

  const startHub = async () => {
    if (hubRef.current) {
      return;
    }

    const hub = createOrderHubConnection();
    hubRef.current = hub;

    hub.on("OrderStatusChanged", () => {
      refreshOrders();
    });

    try {
      await hub.start();
    } catch {
      // Ignore hub start errors; polling + manual refresh still work
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const canceled = await orderService.cancel({ 
        id: orderId 
      });

      if (canceled) {
        toast.success("Pedido cancelado com sucesso!");
        await loadOrders(currentPage);
      }
    } catch (error) {
      toast.error("Erro ao cancelar o pedido, tente novamente mais tarde.");
    }
  };

  const handleShowPaymentModal = (method: PaymentMethod, details: OrderPaymentDetailsResponse) => {
    setSelectedPaymentMethod(method);
    setSelectedPaymentDetails(details);
    setPaymentModalOpen(true);
  };

  const handleTrackShipping = async (orderId: string, orderNumber: number) => {
    try {
      setTrackingLoading(true);
      setTrackingModalOpen(true);
      const data = await orderService.getShippingTracking(orderId);
      setTrackingData(data);
    } catch (error) {
      const message = `Não foi possível consultar o rastreio do pedido #${orderNumber}.`;
      toast.error(message);
      setTrackingData({
        trackingCode: "",
        events: [],
        message
      });
    } finally {
      setTrackingLoading(false);
    }
  };

  const SkeletonBlock = ({ height = 16, width = "100%" }: { height?: number | string; width?: number | string }) => (
    <div className="animate-pulse bg-gray-200 rounded w-full" style={{ height, maxWidth: width }} />
  );

  // Only the first load has nothing to show; a reload keeps the current content on screen
  const isFirstLoad = isLoading && lastUpdated === null;

  const renderSkeleton = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="bg-white rounded-md shadow-sm border border-gray-200 p-4 space-y-3">
            <SkeletonBlock height={18} width={200} />
            <SkeletonBlock height={14} width={260} />
            <SkeletonBlock height={14} width={220} />
            <SkeletonBlock height={14} width={180} />
          </div>
        ))}
      </div>
      <div className="flex justify-center gap-2 items-center">
        {Array.from({ length: 7 }).map((_, index) => (
          <SkeletonBlock key={index} height={40} width={40} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50">
      <SmartNavbar />
      <div className="page-gutter py-4 sm:py-14">
        <div className="flex items-center justify-between gap-3 my-6 sm:my-0 sm:mb-8">
          <PageTitle>{"MEUS PEDIDOS"}</PageTitle>
          {isFirstLoad ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block h-4 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="h-9 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-sm text-gray-600">
                {lastUpdated ? `Atualizado às ${formatDateTimeToBR(lastUpdated)}` : "Carregando..."}
              </div>
              <Button variant="secondary"
                onClick={refreshOrders}
                disabled={isLoading || isRefreshing}
                className="px-3 py-2 text-sm"
              >
                {"Recarregar"}
              </Button>
            </div>
          )}
        </div>
        {isFirstLoad ? (
          renderSkeleton()
        ) : orders.length === 0 ? (
          // Same reserved height as the empty product list, so the footer does not ride up
          <div className={`min-h-[320px] sm:min-h-[480px] flex flex-col items-center justify-center bg-white rounded-md shadow-sm border border-gray-200 p-6 text-center text-gray-600 transition-opacity ${isLoading ? "opacity-60 pointer-events-none" : ""}`}>
            <p className="font-semibold text-primary-600 text-lg">
              {"Você ainda não tem pedidos."}
            </p>
            <p className="mt-2 text-sm">
              {"Explore nossos produtos e faça sua primeira compra."}
            </p>
            <Link
              to={toPublicPath("/produtos")}
              className={buttonClasses("primary", "inline-block mt-4 px-4 py-2")}
            >
              {"Ver produtos"}
            </Link>
          </div>
        ) : (
          // Keep the current page visible while the next one loads so the
          // page height holds and the scroll to the top starts from here
          <div className={`transition-opacity ${isLoading ? "opacity-60 pointer-events-none" : ""}`}>
            {orders.map(order => (
              <OrderCard
                key={order.id}
                id={order.id}
                orderNumber={order.orderNumber}
                status={order.status}
                finalPrice={order.finalPrice}
                shippingMethod={order.shippingMethod}
                shippingDetails={order.shippingDetails}
                createdAt={order.createdAt}
                paymentMethod={order.paymentMethod}
                creditCardInstallments={order.creditCardInstallments}
                creditCardInterest={order.creditCardInterest}
                paymentDetails={order.paymentDetails}
                paymentReceivedAt={order.paymentReceivedAt}
                nfe={order.nfe}
                showXmlDownload={tenantPreference?.enableNfeXmlDownload ?? true}
                onCancel={handleCancelOrder}
                onShowPaymentModal={handleShowPaymentModal}
                onTrackShippingModal={handleTrackShipping}
              />
            ))}
          </div>
        )}
        
        {orders.length > 0 && (
          <>
            <PaginationSummary
              totalItems={totalItems}
              singularLabel={"pedido encontrado"}
              pluralLabel={"pedidos encontrados"}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
      <Footer />
      {selectedPaymentMethod !== null && selectedPaymentDetails && (
        <PaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          paymentMethod={selectedPaymentMethod}
          paymentDetails={selectedPaymentDetails}
        />
      )}
      <ShippingTrackingEventsModal
        isOpen={isTrackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        tracking={trackingData}
        isLoading={isTrackingLoading}
      />
    </div>
  );
};

export default UserOrderPage;
